import { SlugError } from '../console';
import { toHttps, URLLike } from '../url';
import { pipe, cond } from '../utils';
import { isSupportWebp, webpCheck as checkWebp } from '../is';

type ImageObject = string | Blob;

// 判断图片信息类型，获取图片数据
const getImageString = (image: ImageObject) => new Promise<string>((resolve) => {
  if (image instanceof File) {
    const reader = new FileReader();
    reader.onload = function () {
      resolve(this.result as string);
    };
    reader.readAsDataURL(image);
    return;
  }
  // if (typeof image === 'string') {
  //   resolve(image);
  //   return;
  // }
  // resolve(undefined);
  resolve(image as string);
});

interface CheckImageSizeOptions {
  width?: number;
  height?: number;
}

// TODO: 通用化
const checkDeviation = (
  deviation: number,
  target: number,
  value?: number,
) => Math.abs((value ?? target) - target) > deviation;

/**
  * 检测图片尺寸
  * @param {String} data - 图片数据：Data URL
  */
const checkSize = (
  data: string,
  options: CheckImageSizeOptions,
  deviation = 0,
) => new Promise<boolean>((resolve) => {
  const virtualImage = new Image();
  virtualImage.src = data;
  virtualImage.onload = function (this: HTMLImageElement) {
    resolve(!(
      checkDeviation(deviation, this.naturalWidth, options.width)
      || checkDeviation(deviation, this.naturalHeight, options.height)
    ));
  } as any;
});

/**
 * 图片尺寸检查
 * @param {(Object|String)} image - 图片信息，支持 File 对象 或 Data URLs
 * @param {Object} [options={}] - 检查参数
 * @param {Number} [options.width] - 检查宽度
 * @param {Number} [options.height] - 检查高度
 * @param {Number} [deviation=0] - 允许的偏差量
 * @returns {Object} - Promise 对象
 */
export const checkImageSize = async (
  image: ImageObject,
  options: CheckImageSizeOptions,
  deviation = 0,
): Promise<boolean> => {
  const data = await getImageString(image);
  return await checkSize(data, options, deviation);
};

/**
 * 图片优化，暂不支持 gif 图片
 * @param {(Object|String)} image - 图片信息，支持 File 对象 或 Data URLs
 * @param {Number} [quality=0.9] - 输出图片质量，0 - 1 之间，仅 image/jpeg 与 image/webp 有效
 * @param {Object} [options={}] - 输出图片相关选项
 * @param {Number} [options.maxWidth=1920] - 输出图片的最大宽度，若图片原始宽度小于该宽度，则返回原始尺寸图片，若图片原始宽度大于该宽度，则返回等比缩放为该尺寸的图片
 * @param {String} [options.mimeType] - 输出图片格式，MIME 类型
 * @returns {Object} Promise 对象，resolve 函数参数为优化后的图片 Blob 对象，如果输出类型为 image/gif，则原样返回 image 参数内容
 */
export const imageOptimization = (
  image: ImageObject,
  quality = 0.9,
  { maxWidth = 1920, mimeType }: { maxWidth?: number; mimeType?: string } = {},
): Promise<ImageObject> => new Promise((resolve, reject) => {
  /**
     * 转换为 Blob 类型
     * @param {String} data - 图片数据：Data URL
     */
  const toBlob = (data: string) => {
    const type = data.match(/data:([^;,]+)/);
    if (!Array.isArray(type)) {
      reject(new SlugError('非图片类型的 Data URLs', 'sf/image/optimization'));
      return;
    }
    const outputType = mimeType ? mimeType : type[1];

    // 暂不支持 gif 图片，原样返回 image 参数内容
    if (outputType === 'image/gif') {
      return resolve(image);
    }

    const virtualImage = new Image();
    virtualImage.src = data;
    virtualImage.onload = function (this: HTMLImageElement) {
      let width = this.naturalWidth;
      let height = this.naturalHeight;
      if (width > maxWidth) {
        height = Math.round((maxWidth * height) / width);
        width = maxWidth;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
          context?.drawImage(this, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              blob
                ? resolve(blob)
                : reject(new SlugError(
                  'canvas\'s blob is null',
                  'sf/image/optimization',
                ));
            },
            mimeType ? mimeType : type[1],
            quality,
          );
    } as any;
  };

  getImageString(image).then(data => toBlob(data));
});

// === 数据万象 ===

export interface CloudImageURLOptions {
  buckey: CloudImageBuckey;
  url: URLLike; // 需要处理的 url
  rule?: string; // 图片处理规则，不包括 format
  formatJPG?: boolean; // 是否按需将 png, webp 格式转换为 jpg

  formatWebp?: boolean; // 启用 webp 优化
  webpCheck?: string; // 从 window[webpCheck] 检查 webp 支持情况
}

export type CloudImageBuckey = { [k: string]: string };

// 使用数据万象对图片进行处理，文档：https://cloud.tencent.com/document/product/460/6929
// imageView2/<mode>
//    /w/<Width>
//    /h/<Height>
//    /format/<Format>
//    /q/<Quality>
//    /rq/<Quality>
//    /lq/<Quality>
export const cloudImageURL = ({
  buckey,
  url,
  rule = '',
  formatJPG = true,

  formatWebp: preWebp = false,
  webpCheck = '',
}: CloudImageURLOptions) => {
  checkWebp();
  const supportWebp = webpCheck ? !!window[webpCheck as any] : isSupportWebp();
  const formatWebp = preWebp && supportWebp;
  const h = pipe<URL>(
    toHttps,
    // 转发
    (url: URL) => {
      Object.keys(buckey).forEach((host) => {
        if (url.host === host) {
          // eslint-disable-next-line no-param-reassign
          url.host = `${buckey[host]}.image.myqcloud.com`;
        }
      });
      return url;
    },
    // 处理腾讯云图片
    (url: URL) => {
      if (!url.host.includes('myqcloud.com')) {
        return url;
      }
      // eslint-disable-next-line no-param-reassign
      url.host = url.host.replace(/cos.[^.]+/, 'image');

      const [, suffix] = url.pathname.match(/\.([0-9a-zA-Z]+)$/) ?? [];
      const r = cond<string>([
        { cond: formatWebp, fn: rule => `${rule}/format/webp` },
        { cond: suffix === 'bmp', fn: rule => `${rule}/format/jpg` },
        {
          cond: suffix === 'png',
          fn: rule => (formatJPG ? `${rule}/format/jpg` : rule),
        },
        {
          cond: suffix === 'webp',
          // fn: rule => (isiOS ? rule + (format ? '/format/jpg' : '/format/png') : rule),
          fn: rule => (supportWebp
            ? rule
            : rule + (formatJPG ? '/format/jpg' : '/format/png')),
        },
        {
          cond: true,
          // cond: suffix === 'gif' || suffix === 'jpeg' || suffix === 'jpg',
          fn: rule => rule,
        },
        // 没有显式包含 q 规则，则图片质量统一设置为 70%
      ])(/\/q\//.test(rule) ? rule : `${rule}/q/70`);

      const ext = url.searchParams.toString();
      // eslint-disable-next-line no-param-reassign
      url.search = `?imageView2${r}${ext ? `&${ext}` : ''}`;
      return url;
    },
  );
  return h(url).href;
};
